
import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';
import { clearCachedUser, isCachedUserForToken, isSessionEndingError, readCachedUser, saveCachedUser } from '../services/sessionCache';

const parseTimeoutEnv = (rawValue, fallback) => {
  const parsed = Number.parseInt(String(rawValue ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

const AUTH_CRITICAL_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_API_CRITICAL_TIMEOUT_MS, 60000);
const LOGIN_REQUEST_TIMEOUT_MS = parseTimeoutEnv(process.env.REACT_APP_LOGIN_TIMEOUT_MS, Math.max(120000, AUTH_CRITICAL_TIMEOUT_MS));
const VERIFY_REQUEST_TIMEOUT_MS = Math.max(
  AUTH_CRITICAL_TIMEOUT_MS,
  parseTimeoutEnv(process.env.REACT_APP_AUTH_VERIFY_TIMEOUT_MS, 60000)
);
const VERIFY_RETRY_BASE_MS = 5000;
const VERIFY_RETRY_MAX_MS = 60000;

const isRetryableVerificationError = (error) => {
  if (error?.code === 'ERR_CANCELED') return false;
  const status = Number(error?.response?.status || 0);
  return !status || status === 429 || status >= 500;
};

const resolveLoginErrorMessage = (error) => {
  const backendMessage = String(error?.response?.data?.message || '').trim();
  if (backendMessage) return backendMessage;

  const status = Number(error?.response?.status || 0);
  if (status === 401) return 'Invalid email or password.';
  if (status === 429) return 'Too many sign-in attempts. Please wait and try again.';
  if (error?.code === 'ECONNABORTED') return 'Sign-in timed out. Please try again.';
  if (!error?.response) return 'Unable to reach the server. Check your connection and try again.';
  return 'Unable to sign in right now. Please try again.';
};


// Always attach token from localStorage for every request
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    } else {
      delete config.headers['Authorization'];
    }
    return config;
  },
  error => Promise.reject(error)
);

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const cached = readCachedUser()?.user;
    return isCachedUserForToken(cached, localStorage.getItem('token')) ? cached : null;
  });
  const [loading, setLoading] = useState(() => {
    const cached = readCachedUser()?.user;
    return !isCachedUserForToken(cached, localStorage.getItem('token'));
  });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [verificationPending, setVerificationPending] = useState(() => {
    const cached = readCachedUser()?.user;
    return Boolean(isCachedUserForToken(cached, localStorage.getItem('token')));
  });
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [verifyTick, setVerifyTick] = useState(0);
  const [verificationRetryable, setVerificationRetryable] = useState(false);
  const lastVerifyRef = React.useRef(0);
  const verificationFailuresRef = React.useRef(0);
  // `verifyToken` needs the current user only to decide whether a cached profile
  // is trustworthy after a failed verify. Reading `user` directly made the effect
  // depend on it - and because the effect calls setUser on success, that
  // dependency would re-run the whole verification in a loop. A ref gives the
  // async body the latest value without widening the dependency list.
  const userRef = React.useRef(user);
  React.useEffect(() => { userRef.current = user; }, [user]);

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('token');
    clearCachedUser();
    setVerificationPending(false);
    setVerificationFailed(false);
    setVerificationRetryable(false);
    verificationFailuresRef.current = 0;
    lastVerifyRef.current = 0;
    delete api.defaults.headers.common['Authorization'];
  };

  useEffect(() => {
    let active = true;
    const verifyToken = async () => {
      try {
        const response = await api.get('/auth/verify', {
          timeout: VERIFY_REQUEST_TIMEOUT_MS,
          meta: { readRetries: 0 }
        });
        if (!active) return;
        setUser(response.data.data.user);
        saveCachedUser(response.data.data.user);
        setVerificationPending(false);
        setVerificationFailed(false);
        setVerificationRetryable(false);
        verificationFailuresRef.current = 0;
        lastVerifyRef.current = Date.now();
      } catch (error) {
        if (!active) return;
        console.error('Token verification failed:', error);
        if (isSessionEndingError(error) || Number(error?.response?.status) === 423) logout();
        else {
          const cachedUser = readCachedUser()?.user;
          const hasTrustedUser = Boolean(userRef.current) || isCachedUserForToken(cachedUser, token);
          setVerificationPending(true);
          // A cached, still-valid profile keeps the shell usable while verify is
          // retried in the background. Showing "session not verified" beside a
          // working till on a merely slow link sent operators chasing a phantom
          // auth bug instead of waiting for the retry.
          setVerificationFailed(!hasTrustedUser);
          verificationFailuresRef.current += 1;
          setVerificationRetryable(isRetryableVerificationError(error));
          // A first visit has no trustworthy profile to render. Retain the token
          // and show an explicit retry screen rather than a false sign-in page.
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      
      // Only verify if we haven't verified in the last 5 minutes
      const now = Date.now();
      const fiveMinutes = 5 * 60 * 1000;
      if (now - lastVerifyRef.current > fiveMinutes) {
        verifyToken();
      } else {
        // Reuse cached user state, just mark as loaded
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    return () => { active = false; };
  }, [token, verifyTick]);

  // A network outage does not necessarily trigger an `online` event (Wi-Fi can
  // stay connected while the origin is unreachable). Retry with bounded backoff
  // and also wake immediately when connectivity returns or the tab is focused.
  useEffect(() => {
    if (!token || !verificationRetryable || typeof window === 'undefined') return undefined;
    let timer;
    let stopped = false;
    const delayMs = Math.min(VERIFY_RETRY_MAX_MS, VERIFY_RETRY_BASE_MS * (2 ** Math.min(verificationFailuresRef.current - 1, 4)));
    const retry = () => {
      if (stopped) return;
      if (typeof navigator !== 'undefined' && navigator.onLine === false) {
        clearTimeout(timer);
        timer = setTimeout(retry, VERIFY_RETRY_MAX_MS);
        return;
      }
      stopped = true;
      setVerificationRetryable(false);
      lastVerifyRef.current = 0;
      setVerifyTick((tick) => tick + 1);
    };
    timer = setTimeout(retry, delayMs);
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    return () => {
      stopped = true;
      clearTimeout(timer);
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
    };
  }, [token, verificationRetryable]);

const login = async (identifier, password) => {
  try {
    const response = await api.post('/auth/login', { identifier, password }, {
      timeout: LOGIN_REQUEST_TIMEOUT_MS,
      meta: {
        suppressToast: true,
        skipAuthRedirect: true
      }
    });
    // Try different possible response structures
    let newToken, userData;
    
    if (response.data.data) {
      // Expected structure: { success: true, data: { access_token: '...', user: {...} } }
      newToken = response.data.data.access_token;
      userData = response.data.data.user;
    } else if (response.data.access_token) {
      // Alternative structure: { access_token: '...', user: {...} }
      newToken = response.data.access_token;
      userData = response.data.user;
    } else if (response.data.token) {
      // Another alternative: { token: '...', user: {...} }
      newToken = response.data.token;
      userData = response.data.user;
    }
    
    
    if (!newToken) {
      throw new Error('No token received from server');
    }
    
    // Set token first, then user - this ensures isAuthenticated becomes true immediately
    localStorage.setItem('token', newToken);
    
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
    
    setToken(newToken);
    setUser(userData);
    saveCachedUser(userData);
    setVerificationPending(false);
    setVerificationFailed(false);
    setVerificationRetryable(false);
    verificationFailuresRef.current = 0;
    lastVerifyRef.current = Date.now();

    setLoading(false);
    return { success: true };
  } catch (error) {
    setLoading(false);
    console.error('[AuthContext] login() error response:', error.response);
    return {
      success: false,
      error: resolveLoginErrorMessage(error)
    };
  }
};  const updateUser = (userData) => {
    setUser(prev => ({ ...prev, ...userData }));
  };  const value = {
    user,
    token,
    loading,
    login,
    logout,
    updateUser,
    verificationPending,
    verificationFailed,
    retryVerification: () => {
      setVerificationRetryable(false);
      if (!user) {
        setVerificationPending(false);
        setLoading(true);
      }
      lastVerifyRef.current = 0;
      setVerifyTick((tick) => tick + 1);
    },
    isAuthenticated: !!token && !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
